import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Layers,
  Building2,
  Users,
  Globe,
  ShoppingCart,
  Droplets,
  Truck,
  Wrench,
  ExternalLink,
  Settings
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category definitions
const CATEGORIES = [
  { 
    id: 'truck_repair', 
    name: 'Mechanic Shop', 
    icon: Wrench, 
    color: 'blue',
    description: 'Auto repair, truck repair, service centers',
    features: ['Invoicing', 'Parts Inventory', 'Customer Management', 'Work Orders']
  },
  { 
    id: 'wholesale', 
    name: 'Wholesale / Import', 
    icon: Globe, 
    color: 'slate',
    description: 'Import/export businesses, wholesale distribution',
    features: ['Purchase Orders', 'Shipment Tracking', 'Landed Cost', 'Supplier Management']
  },
  { 
    id: 'grocery_store', 
    name: 'Grocery Store', 
    icon: ShoppingCart, 
    color: 'emerald',
    description: 'Grocery stores, supermarkets, convenience stores',
    features: ['POS System', 'Inventory with Buy/Sell Cost', 'Barcode Scanner', 'Profit Margins']
  },
];

const getColorClasses = (color, isDark) => {
  const colors = {
    blue: { bg: isDark ? 'bg-blue-900/30' : 'bg-blue-50', border: isDark ? 'border-blue-700' : 'border-blue-200', text: isDark ? 'text-blue-400' : 'text-blue-600', badge: 'bg-blue-100 text-blue-700' },
    slate: { bg: isDark ? 'bg-slate-700' : 'bg-slate-50', border: isDark ? 'border-slate-600' : 'border-slate-200', text: isDark ? 'text-slate-300' : 'text-slate-600', badge: 'bg-slate-100 text-slate-700' },
    emerald: { bg: isDark ? 'bg-emerald-900/30' : 'bg-emerald-50', border: isDark ? 'border-emerald-700' : 'border-emerald-200', text: isDark ? 'text-emerald-400' : 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-700' },
    sky: { bg: isDark ? 'bg-sky-900/30' : 'bg-sky-50', border: isDark ? 'border-sky-700' : 'border-sky-200', text: isDark ? 'text-sky-400' : 'text-sky-600', badge: 'bg-sky-100 text-sky-700' },
    amber: { bg: isDark ? 'bg-amber-900/30' : 'bg-amber-50', border: isDark ? 'border-amber-700' : 'border-amber-200', text: isDark ? 'text-amber-400' : 'text-amber-600', badge: 'bg-amber-100 text-amber-700' },
  };
  return colors[color] || colors.blue;
};

export default function AdminCategories() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [businessesRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/businesses`),
        axios.get(`${API}/admin/users`)
      ]);
      setBusinesses(businessesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const getBusinessesForCategory = (categoryId) => {
    return businesses.filter(b => (b.category || 'truck_repair') === categoryId);
  };

  const getUsersForCategory = (categoryId) => {
    const categoryBusinessIds = getBusinessesForCategory(categoryId).map(b => b.id);
    return users.filter(u => categoryBusinessIds.includes(u.business_id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-categories">
      <div>
        <h1 className={`text-2xl md:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
          Categories
        </h1>
        <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          Business categories and their settings
        </p>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CATEGORIES.map((category) => {
          const Icon = category.icon;
          const colors = getColorClasses(category.color, isDark);
          const categoryBusinesses = getBusinessesForCategory(category.id);
          const categoryUsers = getUsersForCategory(category.id);

          return (
            <Card 
              key={category.id} 
              className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} overflow-hidden`}
            >
              {/* Header */}
              <div className={`p-4 ${colors.bg} border-b ${colors.border}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                      <Icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {category.name}
                      </h3>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {category.description}
                      </p>
                    </div>
                  </div>
                  {category.externalUrl && (
                    <a href={category.externalUrl} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className={`${isDark ? 'border-slate-600' : ''}`}>
                        <ExternalLink className="w-4 h-4 mr-1" /> Open App
                      </Button>
                    </a>
                  )}
                </div>
              </div>

              <CardContent className="p-4">
                {/* Stats */}
                <div className="flex gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Building2 className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                      {categoryBusinesses.length}
                    </span>
                    <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>companies</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                      {categoryUsers.length}
                    </span>
                    <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>users</span>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-4">
                  <p className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>FEATURES</p>
                  <div className="flex flex-wrap gap-1">
                    {category.features.map((feature, idx) => (
                      <Badge key={idx} className={`text-xs ${colors.badge}`}>
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Companies Preview */}
                {categoryBusinesses.length > 0 && (
                  <div>
                    <p className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>COMPANIES</p>
                    <div className="space-y-1">
                      {categoryBusinesses.slice(0, 3).map((business) => (
                        <Link 
                          key={business.id}
                          to={`/${business.slug}`}
                          className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} transition-colors`}
                        >
                          <div className="flex items-center gap-2">
                            {business.logo ? (
                              <img src={business.logo} alt="" className="w-6 h-6 rounded object-cover" />
                            ) : (
                              <div className={`w-6 h-6 rounded flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                                <Building2 className="w-3 h-3 text-slate-400" />
                              </div>
                            )}
                            <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-700'}`}>{business.name}</span>
                          </div>
                          <ExternalLink className={`w-3 h-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                        </Link>
                      ))}
                      {categoryBusinesses.length > 3 && (
                        <p className={`text-xs text-center py-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          +{categoryBusinesses.length - 3} more
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {categoryBusinesses.length === 0 && (
                  <p className={`text-sm text-center py-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    No companies in this category yet
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
